import {
  Avatar,
  Box,
  Button,
  Grid,
  Typography,
  Badge,
  CircularProgress,
} from "@mui/material";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import { useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import ConfirmModal from "components/ConfirmModal/ConfirmModal";
import { useAuth } from "context/authContext";
import { useAuthData } from "modules/Authentication/hooks/useAuthData";
import { CartService } from "services/CartService";
import { CartItem } from "types";

const UserProfile = () => {
  const { currentUser, isLoggedIn, isQuantityUpdate } = useAuth();
  const { onSignOut } = useAuthData();

  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [isLogoutModalOpen, setLogoutModalOpen] = useState(false);
  const [cartCount, setCartCount] = useState(0);
  const [loading, setLoading] = useState(true);

  const isMyLearningPath = location.pathname === "/my-learning";

  useEffect(() => {
    const fetchCartCount = async () => {
      if (isLoggedIn && currentUser) {
        setLoading(true); // Start loader
        try {
          const cartData = await CartService.getCartItems(currentUser.uid);

          // Ensure cartData.items is treated as CartItem[]
          const items = (cartData?.items || []) as CartItem[];

          const totalQuantity = items.reduce((sum: number, item: CartItem) => {
            // Safely access quantity (default to 0 if undefined)
            return sum + (item.quantity ?? 0);
          }, 0);

          setCartCount(totalQuantity);
        } catch (error) {
          console.error("Error fetching cart count:", error);
        } finally {
          setLoading(false); // Stop loader
        }
      }
    };

    fetchCartCount();
  }, [currentUser, isLoggedIn, isQuantityUpdate]);

  const handleProfileView = () => {
    navigate("/profile-view");
  };

  const handleClick = () => {
    setOpen(!open);
  };

  const handleCheckOut = () => {
    navigate(`/cart`);
  };

  const handleOpenModal = () => {
    setLogoutModalOpen(true);
  };
  const handleCloseModal = () => {
    setLogoutModalOpen(false);
  };

  const handleConfirmLogout = () => {
    onSignOut();
    setLogoutModalOpen(false);
    setOpen(false);
    navigate("/");
  };

  const handleNavigateLogin = () => {
    navigate("/login");
  };

  const handleNavigateSignup = () => {
    navigate("/register");
  };

  return (
    <>
      <>
        <ConfirmModal
          title="You’re About to Log Out"
          // subtitle="  Figma ipsum component variant main layer. Line horizontal arrange team editor thumbnail distribute. Follower figma blur connection slice plugin. Text mask hand bullet ipsum slice mask selection bullet list. Polygon align hand bold."
          // subtitle=""
          cancelText="Cancel"
          confirmText="Confirm"
          titleColor="#233F91"
          isLoading={false}
          onCancel={handleCloseModal}
          onConfirm={handleConfirmLogout}
          isConfirmModal={isLogoutModalOpen}
        />
      </>
      <Grid
        mr={{ xs: 1, md: 0 }}
        mt={{ xs: 1, md: 0 }}
        position={"relative"}
        display={"flex"}
        gap={{ xs: 2, sm: 5 }}
        alignItems={"center"}
        width={"100%"}
      >
        {!isLoggedIn && (
          <Box
            display={"flex"}
            alignItems={"center"}
            gap={{ xs: 2, sm: 1 }}
            px={2}
            width={"100%"}
          >
            <Box
              display={"flex"}
              width={"100%"}
              justifyContent={"center"}
              alignItems={"center"}
            >
              <Box display={"flex"} justifyContent={"center"} width={"100%"}>
                {/* {isLoggedIn && ( */}
                <Box
                  component="img"
                  src="/assets/icons/moblogoWhite.svg"
                  alt="logo"
                  sx={{
                    width: { xs: "112.94px", md: "112px" },
                    // height: { xs: "100.86px", md: "54.67px" },
                    objectFit: "contain",
                    ml: 5,
                    display: { xs: "flex", md: "none" },
                  }}
                ></Box>
                {/* )} */}
              </Box>
              <Typography
                fontWeight={400}
                fontSize={17}
                width={"40%"}
                color={"#EDEDED"}
                display={{ xs: "none", md: "block" }}
              >
                Become a Content Creator
              </Typography>
              <Box display={"flex"} justifyContent={"flex-end"}>
                <Button sx={styleSheet.loginBtn} onClick={handleNavigateLogin}>
                  Log in
                </Button>
              </Box>
            </Box>

            <Button
              sx={{
                ...styleSheet.signupBtn,
                display: { xs: "none", md: "block" },
              }}
              onClick={handleNavigateSignup}
            >
              Sign Up
            </Button>
          </Box>
        )}
        {isLoggedIn && (
          <Box
            display={"flex"}
            justifyContent={"center"}
            width={"100%"}
            alignItems={"center"}
          ></Box>
        )}
        {/* Conditionally render user profile information if the user is logged in */}
        {isLoggedIn && (
          <>
            <Box
              display={"flex"}
              justifyContent={"center"}
              width={"100%"}
              alignItems={"center"}
            >
              <Box
                component="img"
                src="/assets/icons/moblogoWhite.svg"
                alt="logo"
                sx={{
                  display: { xs: "flex", md: "none" },
                }}
              ></Box>
            </Box>
            <Box
              position={"relative"}
              display={"flex"}
              justifyContent={"flex-end"}
              width={"100%"}
            >
              {loading ? ( // Show loader if data is still being fetched
                <CircularProgress
                  size={18}
                  color="success"
                  sx={{ position: "absolute", top: "-5px", right: "-3px" }}
                />
              ) : (
                <Badge
                  badgeContent={cartCount}
                  color="error"
                  overlap="circular"
                  sx={{
                    cursor: "pointer",
                    position: "absolute",
                    top: { xs: 0, md: "3px" },
                    right: "3px",
                  }}
                ></Badge>
              )}
              <ShoppingCartIcon
                onClick={handleCheckOut}
                sx={{
                  color: "white",
                  cursor: "pointer",
                  width: { xs: "25.28px", sm: "27px" },
                  height: { xs: "25.14px", sm: "27px" },
                }}
              />
            </Box>

            <Grid
              sx={{ cursor: "pointer" }}
              display={"flex"}
              alignItems={"center"}
              gap={1}
              onClick={handleClick}
            >
              <Avatar
                alt={currentUser?.displayName || ""}
                src={
                  currentUser?.userInfo?.profileUrl
                    ? currentUser?.userInfo?.profileUrl
                    : "/assets/icons/usericon.svg"
                }
                sx={{ width: { xs: 28, sm: 37 }, height: { xs: 28, sm: 37 } }}
              />

              <Box
                display={{ xs: "none", sm: "flex" }}
                flexDirection={"column"}
                color={"white"}
              >
                <Box
                  display={"flex"}
                  alignItems={"center"}
                  gap={"5px"}
                  fontSize={"18px"}
                  fontWeight={700}
                  fontFamily="Atten Round New"
                >
                  <Box
                    display={"flex"}
                    alignItems={"center"}
                    gap={"5px"}
                    fontSize={"18px"}
                    fontWeight={700}
                    fontFamily="Atten Round New"
                    width={"90px"}
                  >
                    {currentUser?.userInfo?.firstName}{" "}
                    {currentUser?.userInfo?.lastName}
                  </Box>

                  <img
                    src="/assets/icons/proArrow.png"
                    alt="arrow"
                    style={{ width: "24px", height: "24px" }}
                  />
                </Box>
              </Box>
            </Grid>
          </>
        )}

        {/* // } */}
        {open && (
          <Box sx={styleSheet.box}>
            <Box display={"flex"} flexDirection={"column"} gap={2}>
              <Typography
                onClick={handleOpenModal}
                sx={{ cursor: "pointer" }}
                fontSize={"14px"}
                fontWeight={500}
                color={"#505050"}
              >
                logout
              </Typography>{" "}
            </Box>
          </Box>
        )}
      </Grid>
    </>
  );
};

export default UserProfile;

const styleSheet = {
  dowIcon: {
    position: "absolute",
    bottom: 0,
    right: 2,
    width: "20.12px",
    height: "20.12px",
  },
  box: {
    position: "absolute",
    top: "35px",
    zIndex: 1,
    right: "10px",
    width: 115,
    bgcolor: "background.paper",
    boxShadow: "0px 4px 4px rgba(0, 0, 0, 0.25)",
    borderRadius: "8px",
    border: "0.5px solid #EDEDED",
    p: 2,
  },
  loginBtn: {
    color: "#00A79D",
    bgcolor: "white",
    width: { xs: "82px", md: "114px" },
    height: { xs: "31px", md: "42px" },
    border: "1px solid #00A79D",
    borderRadius: { xs: "10.81px", md: "15px" },
    fontSize: "14px",
    fontWeight: "500",
    "&:hover": {
      bgcolor: "white",
      color: "#00A79D",
    },
  },
  signupBtn: {
    color: "#FFFFFF",
    bgcolor: "#00A79D",
    width: "114px",
    height: "42px",
    border: "1px solid #0000001A",
    borderRadius: "15px",
    fontSize: "14px",
    fontWeight: "600",
    "&:hover": {
      bgcolor: "#00A79D",
      color: "#fff",
    },
  },
};
